// Copyright (C) 2023-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { Row } from 'antd/lib/grid';
import Text from 'antd/lib/typography/Text';
import notification from 'antd/lib/notification';
import CVATLoadingSpinner from 'components/common/loading-spinner';
import ReferenceJobItem from 'components/reference-job-item/reference-job-item';
import {
    ReferenceJob, ReferenceJobType, QualityReport, QualitySettings, Reference, getCore,
} from 'cvat-core-wrapper';
import React, { useEffect, useReducer } from 'react';
import { useIsMounted } from 'utils/hooks';
import { ActionUnion, createAction } from 'utils/redux';
import EmptyGtJob from './empty-reference-job';
import GtConflicts from './gt-conflicts';
import Issues from './issues';
import ReferenceJobList from './reference-job-list';
import MeanQuality from './mean-quality';
import QualitySettingsModal from '../shared/quality-settings-modal';

const core = getCore();

interface Props {
    reference: Reference;
    onReferenceJobUpdate: (job: ReferenceJob) => void;
}

interface State {
    fetching: boolean;
    referenceReport: QualityReport | null;
    jobsReports: QualityReport[];
    qualitySettings: {
        settings: QualitySettings | null;
        fetching: boolean;
        visible: boolean;
    };
}

enum ReducerActionType {
    SET_FETCHING = 'SET_FETCHING',
    SET_REFERENCE_REPORT = 'SET_REFERENCE_REPORT',
    SET_JOBS_REPORTS = 'SET_JOBS_REPORTS',
    SET_QUALITY_SETTINGS = 'SET_QUALITY_SETTINGS',
    SET_QUALITY_SETTINGS_VISIBLE = 'SET_QUALITY_SETTINGS_VISIBLE',
    SET_QUALITY_SETTINGS_FETCHING = 'SET_QUALITY_SETTINGS_FETCHING',
}

export const reducerActions = {
    setFetching: (fetching: boolean) => createAction(ReducerActionType.SET_FETCHING, { fetching }),
    setReferenceReport: (qualityReport: QualityReport) => createAction(ReducerActionType.SET_REFERENCE_REPORT, { qualityReport }),
    setJobsReports: (qualityReports: QualityReport[]) => createAction(ReducerActionType.SET_JOBS_REPORTS, { qualityReports }),
    setQualitySettings: (qualitySettings: QualitySettings) => createAction(ReducerActionType.SET_QUALITY_SETTINGS, { qualitySettings }),
    setQualitySettingsVisible: (visible: boolean) => createAction(ReducerActionType.SET_QUALITY_SETTINGS_VISIBLE, { visible }),
    setQualitySettingsFetching: (fetching: boolean) => createAction(ReducerActionType.SET_QUALITY_SETTINGS_FETCHING, { fetching }),
};

const reducer = (state: State, action: ActionUnion<typeof reducerActions>): State => {
    if (action.type === ReducerActionType.SET_FETCHING) {
        return {
            ...state,
            fetching: action.payload.fetching,
        };
    }

    if (action.type === ReducerActionType.SET_REFERENCE_REPORT) {
        return {
            ...state,
            referenceReport: action.payload.qualityReport,
        };
    }

    if (action.type === ReducerActionType.SET_JOBS_REPORTS) {
        return {
            ...state,
            jobsReports: action.payload.qualityReports,
        };
    }

    if (action.type === ReducerActionType.SET_QUALITY_SETTINGS) {
        return {
            ...state,
            qualitySettings: {
                ...state.qualitySettings,
                settings: action.payload.qualitySettings,
            },
        };
    }

    if (action.type === ReducerActionType.SET_QUALITY_SETTINGS_VISIBLE) {
        return {
            ...state,
            qualitySettings: {
                ...state.qualitySettings,
                visible: action.payload.visible,
            },
        };
    }

    if (action.type === ReducerActionType.SET_QUALITY_SETTINGS_FETCHING) {
        return {
            ...state,
            qualitySettings: {
                ...state.qualitySettings,
                fetching: action.payload.fetching,
            },
        };
    }

    return state;
};

function ReferenceQualityComponent(props: Props): JSX.Element {
    const { reference, onReferenceJobUpdate } = props;
    const isMounted = useIsMounted();

    const [state, dispatch] = useReducer(reducer, {
        fetching: true,
        referenceReport: null,
        jobsReports: [],
        qualitySettings: {
            settings: null,
            fetching: true,
            visible: false,
        },
    });

    useEffect(() => {
        dispatch(reducerActions.setFetching(true));
        dispatch(reducerActions.setQualitySettingsFetching(true));

        function handleError(error: Error): void {
            if (isMounted()) {
                notification.error({
                    description: error.toString(),
                    message: 'Could not initialize quality analytics page',
                });
            }
        }

        core.analytics.quality
            .reports({ pageSize: 1, target: 'reference', referenceID: reference.id })
            .then(([report]) => {
                let reportRequest = Promise.resolve<QualityReport[]>([]);
                if (report) {
                    reportRequest = core.analytics.quality.reports({
                        pageSize: reference.jobs.length,
                        parentID: report.id,
                        target: 'job',
                    });
                }
                const settingsRequest = core.analytics.quality.settings.get({ referenceID: reference.id });

                Promise.all([reportRequest, settingsRequest])
                    .then(([jobReports, settings]) => {
                        dispatch(reducerActions.setQualitySettings(settings));
                        dispatch(reducerActions.setReferenceReport(report || null));
                        dispatch(reducerActions.setJobsReports(jobReports));
                    })
                    .catch(handleError)
                    .finally(() => {
                        dispatch(reducerActions.setQualitySettingsFetching(false));
                        dispatch(reducerActions.setFetching(false));
                    });
            })
            .catch(handleError);
    }, [reference?.id]);

    const gtJob = reference.jobs.find((job: ReferenceJob) => job.type === ReferenceJobType.GROUND_TRUTH);

    const {
        fetching,
        referenceReport,
        jobsReports,
        qualitySettings: {
            settings: qualitySettings,
            fetching: qualitySettingsFetching,
            visible: qualitySettingsVisible,
        },
    } = state;

    return (
        <div className='cvat-reference-quality-page'>
            {fetching ? (
                <CVATLoadingSpinner size='large' />
            ) : (
                <>
                    {gtJob ? (
                        <>
                            <Row>
                                <MeanQuality
                                    referenceReport={referenceReport}
                                    setQualitySettingsVisible={(visible) => dispatch(reducerActions.setQualitySettingsVisible(visible))}
                                    referenceID={reference.id}
                                />
                            </Row>
                            <Row gutter={16}>
                                <GtConflicts referenceReport={referenceReport} />
                                <Issues reference={reference} />
                            </Row>
                            {!(gtJob && gtJob.stage === 'acceptance' && gtJob.state === 'completed') ? (
                                <Row>
                                    <Text type='secondary' className='cvat-reference-quality-reports-hint'>
                                        Quality reports are not computed unless the GT job is in the&nbsp;
                                        <strong>completed state</strong>
                                        &nbsp;and&nbsp;
                                        <strong>acceptance stage.</strong>
                                    </Text>
                                </Row>
                            ) : null}
                            <Row>
                                <ReferenceJobItem
                                    job={gtJob}
                                    reference={reference}
                                    onJobUpdate={onReferenceJobUpdate}
                                />
                            </Row>
                            <Row>
                                <ReferenceJobList jobsReports={jobsReports} reference={reference} />
                            </Row>
                        </>
                    ) : (
                        <Row justify='center'>
                            <EmptyGtJob referenceID={reference.id} />
                        </Row>
                    )}
                    <QualitySettingsModal
                        fetching={qualitySettingsFetching}
                        qualitySettings={qualitySettings}
                        setQualitySettings={(settings) => dispatch(reducerActions.setQualitySettings(settings))}
                        visible={qualitySettingsVisible}
                        setVisible={(visible) => dispatch(reducerActions.setQualitySettingsVisible(visible))}
                    />
                </>
            )}
        </div>
    );
}

export default React.memo(ReferenceQualityComponent);
